import {
  Color,
  DoubleSide,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  MeshStandardMaterial,
  Object3D,
  Vector3,
} from 'three';
import _ from 'underscore';

import {
  CellGridVolumeModel,
  PrismVolumeModel,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { WebApi } from '../../services/web-api';
import {
  CloudFrontSigner,
  toCloudFrontSignerParameters,
} from '../../utils/cloud-front-signer/cloud-front-signer';
import { t } from '../../utils/i18-next';
import { getMaterials } from '../../utils/three/material';
import { transformInternalMeshPositions } from '../../utils/three/mesh';
import { CoordinateManager } from '../coordinate-manager';
import { GltfLoader } from '../loader/gltf';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { clamp, Point2d } from '../utilities/math-helpers';
import { AssetLoadingStatus, AssetType, ElementObj } from './element-obj';
import { HasVolumes } from './interfaces/has-cell-grid-volumes';
import { HasOpacity } from './interfaces/has-opacity';
import { CellGridVolume } from './volume/cell-grid-volume';
import { PrismVolume } from './volume/prism-volume';
import { VolumeHandler } from './volume/volume-handler';

type TerrainModelOptions = {
  timelineContainerId: string;
  timelineContainerIndex: number;
  timelineContainerComment?: string;
  geodataId: string;
  cellGridVolumes: CellGridVolumeModel[];
  prismVolumes: PrismVolumeModel[];
  skyMapVolumeSourceIds: string[];
};

export class TerrainModel extends ElementObj implements HasOpacity, HasVolumes {
  private _opacity = 1;
  onVisibilityChanged?: () => void;

  get assetType(): AssetType {
    return 'terrain-model';
  }
  get opacity() {
    return this._opacity;
  }

  set opacity(value: number) {
    this.setOpacity(value);
    this._opacity = value;
  }

  get glbData() {
    return this._glbData;
  }

  private _glbData?: Mesh;
  geoDataId: string;

  /**
   * The vertices in the GLB model are offset by this amount
   * from the original origo that the model was loaded within
   */
  private vertexOffset: Vector3 = new Vector3();

  /**
   * From the GLB's scene to the mesh as loaded: its node's own transform undone, and the vertices
   * moved as transformInternalMeshPositions moved them.
   */
  private glbSceneToMesh = new Matrix4();

  private wireframe?: LineSegments;

  cellGridVolumes: CellGridVolume[] = [];
  prismVolumes: PrismVolume[] = [];
  skyMapVolumeSourceIds: string[] = [];
  volumeHandler = new VolumeHandler(this);

  constructor(data: TerrainModelOptions) {
    super(data.timelineContainerId);
    this.geoDataId = data.geodataId;
    this.cellGridVolumes = CellGridVolume.fromPropertyArray(data.cellGridVolumes);
    this.prismVolumes = PrismVolume.fromPropertyArray(data.prismVolumes);
    this.skyMapVolumeSourceIds = data.skyMapVolumeSourceIds ?? [];
    this.volumeHandler.activeVolume = this.cellGridVolumes[0] ?? this.prismVolumes[0];

    this.name = `${data.timelineContainerComment || t('type.terrainModel', { ns: 'skyviewVolumeCalculation' })} (${
      data.timelineContainerIndex < 0
        ? t('terrainModel.disconnectedFromTimeline', { ns: 'skyviewAssetMenu' })
        : `${t('terrainModel.timeline', { ns: 'skyviewAssetMenu' })} ${data.timelineContainerIndex}`
    })`;

    this.freeze = {
      translate: true,
      rotateZ: true,
      rotateXY: true,
      scale: true,
    };

    // Ensure composite visibility keeps terrain hidden by default
    void this.setStateVisibility(false);
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'loaded';
  }

  async onStateVisibilityChanged() {
    try {
      if (this.visible && this.loadingStatus !== 'loading') {
        await this.loadModelData();
      }
    } catch (err) {
      this.visible = false;
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
    }

    this.onVisibilityChanged?.();
  }

  getSaveData() {
    return Promise.resolve();
  }

  save() {
    return Promise.resolve();
  }

  async loadModelData() {
    if (this._glbData || this.loadingStatus === 'loading') {
      return;
    }

    this.loadingStatus = 'loading';

    const gltf = await this.downloadGlbData();
    this._glbData = gltf.scene.children[0] as Mesh;

    const nodePosition = this._glbData.position.clone();
    this._glbData.updateMatrix();
    const nodeMatrix = this._glbData.matrix.clone();
    transformInternalMeshPositions(this._glbData);
    // The vertices were moved by the position the node was given, if it had none.
    const shift = this._glbData.position.clone().sub(nodePosition);
    this.glbSceneToMesh = new Matrix4()
      .makeTranslation(shift.negate())
      .multiply(nodeMatrix.invert());

    this._glbData.material = new MeshPhongMaterial({
      color: new Color(0x4c4cff),
      transparent: true,
      flatShading: true,
      side: DoubleSide,
      shininess: 0,
    });
    this.vertexOffset = new Vector3(
      -this._glbData.position.x,
      -this._glbData.position.y,
      this._glbData.position.z,
    );

    this.addWireframe(this._glbData);

    this.lastOffset = this.getOffset();
    const diff = this.lastOffset.clone().sub(this.vertexOffset);
    this._glbData.position.set(diff.x, diff.y, diff.z);

    this.add(this._glbData);

    this.loadingStatus = 'loaded';
  }

  async downloadGlbData() {
    const signedRequestResponse = await SkyMapAxiosServiceFactory.instance
      .createTerrainModelServiceV0()
      .fetchSignRequestParameters({
        path: { geodataId: this.geoDataId },
      });

    const signer = new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse));

    // Terrain models will always have same data path,
    // for other datasets tiles (yaml) will need to be parsed
    const signedRequest = signer.getSignedRequest('geometry/geometry.glb');
    const arrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(signedRequest);
    return GltfLoader.instance.loadGltf(Buffer.from(arrayBuffer));
  }

  /**
   * Gets the matrix from the GLB's scene, its node transforms applied, to local coordinates as the
   * model is shown, for the CAD drawing report. Undefined until it's loaded.
   */
  getSceneToLocalMatrix(): Matrix4 | undefined {
    if (!this._glbData) {
      return undefined;
    }

    this._glbData.updateWorldMatrix(true, false);
    return this._glbData.matrixWorld.clone().multiply(this.glbSceneToMesh);
  }

  getOffset() {
    const localOrigo = CoordinateManager.getInstance().projectToLocal(0, 0);
    return new Vector3(localOrigo.x, localOrigo.y, -CoordinateManager.getInstance().getMAS());
  }

  addWireframe(data: Mesh) {
    this.wireframe = new LineSegments(
      data.geometry,
      new MeshBasicMaterial({
        color: new Color(0, 0, 0),
        wireframe: true,
        transparent: true,
      }),
    );
    this.wireframe.visible = false;
    data.add(this.wireframe);
  }

  setOpacity(opacity: number) {
    this.traverse((node: Object3D) => {
      for (const material of getMaterials(node)) {
        const nodeOpacity =
          opacity >= 1
            ? 1
            : (material as MeshStandardMaterial).wireframe === true
              ? clamp(opacity * 2, 0, 1)
              : opacity / 2;
        material.opacity = nodeOpacity;
        material.transparent = nodeOpacity < 1;
        material.depthWrite = nodeOpacity === 1;
        material.depthTest = true;
        material.needsUpdate = true;
      }
    });
  }

  onSelect() {
    super.onSelect();
    if (this.wireframe) {
      this.wireframe.visible = true;
    }
  }

  onDeselect() {
    super.onDeselect();
    if (this.wireframe) {
      this.wireframe.visible = false;
    }
  }

  getNativeAssetData() {
    return {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
    };
  }

  getGlbVertexGeographicalOffset(): Vector3 {
    return new Vector3(this.vertexOffset.x, this.vertexOffset.y, 0);
  }

  equals(terrainModel: TerrainModel) {
    const sameId = this.geoDataId === terrainModel.geoDataId;
    const sameVolumeSourceIds = _.isEqual(
      this.skyMapVolumeSourceIds.sort(),
      (terrainModel.skyMapVolumeSourceIds ?? []).sort(),
    );

    return sameId && sameVolumeSourceIds;
  }

  private lastOffset: Vector3 = new Vector3();

  /**
   * Used to recalculate the position when switching scenes.
   */
  calculateLocalPosition(): Point2d | undefined {
    const newOffset = this.getOffset();

    if (this.lastOffset.equals(newOffset)) {
      return;
    }

    const diff = CoordinateManager.getInstance().origoDifference;
    this.lastOffset = newOffset;

    this.traverse((child) => {
      if (child.uuid === this.glbData?.uuid) {
        child.position.add(diff);
      }
    });
  }
}
